import type { Edge, LineID, Vertex } from '@metro/graph'
import { EdgeType, Graph } from '@metro/graph'
import type { AlgorithmConfig } from './config'
import { DEFAULT_CONFIG } from './config'
import {
  ruleNoRevisit,
  ruleNoNaptanIdRevisit,
  ruleNoLineReentry,
  ruleMaxTransfers,
  ruleNoIndirectTransferWhenDirectExists,
  ruleNoDegenerateWalkTransfer,
  ruleNoChainedWalks,
  ruleNoSameLineLoop,
  ruleMaxTimeRatio,
  dedupBySignature,
} from './exclusion'
import { edgesToRoute } from './convert'
import { scoreLess } from './ranking'
import type { Route } from './route'
import { recomputeID } from './route'
import { yenK, pathWeight } from './yen'
import { RoutingError } from './astar'
import { transferPenalisedGraph } from './transferPenalisedGraph'

export interface RouteOptions {
  readonly config?: AlgorithmConfig
  readonly stationOrder?: Record<LineID, readonly string[]>
  /**
   * NaPTANs of the user's destination complex (the stations sharing its
   * slug). When non-empty, the post-process pass drops a trailing transfer /
   * walk leg if the prior ride already lands at one of them — the
   * destination is reached, the extra hop is wasted UI noise. See
   * `dropTrailingClusterTransfer` below.
   */
  readonly dstNaptanIds?: ReadonlySet<string>
  /**
   * When `true`, the search runs on a copy of the graph WITHOUT
   * `WalkTransfer` edges, producing pure-transit-only routes (rides + in-station transfers only,
   * no pedestrian segments between separate stations). Used as a
   * supplementary search pass when the primary result set has no
   * pure-transit option — covers users carrying luggage or using a
   * wheelchair who can't take the walk legs the adapter would otherwise
   * surface as "optimal".
   */
  readonly excludeWalkTransfers?: boolean
  /**
   * When positive, the search ranks paths as if every change (transfer or
   * walk edge) took this many extra seconds — TfL's "fewest changes"
   * preference; routes keep their real times. A supplementary pass: the
   * time-ordered K shortest paths of the v2 map are near-identical
   * multi-change permutations, and a one-change journey slower by minutes
   * never makes the budget (TASK-986). Mirrors iOS / Android
   * `RouteOptions.transferPenaltySec`.
   */
  readonly transferPenaltySec?: number
}

/**
 * If a route's last leg is a transfer/walk and the prior ride leg's
 * final stop already lands at a station of the destination complex
 * (`dstNaptanIds`), drop the trailing leg. RouteCard displays the
 * cluster name (shared across both records by design), so the user
 * sees the same destination either way — minus a meaningless transfer.
 *
 * Returns a NEW route with `transfer_count`, `total_time_s`,
 * `walking_time_s` updated and `id` recomputed. Returns the input
 * unchanged when no truncation applies.
 */
function dropTrailingClusterTransfer(r: Route, dstNaptanIds: ReadonlySet<string>): Route {
  if (r.legs.length < 2) return r
  const last = r.legs[r.legs.length - 1]
  if (last.type !== 'transfer' && last.type !== 'walk') return r
  const prior = r.legs[r.legs.length - 2]
  if (prior.type !== 'ride') return r
  const stops = prior.stops ?? []
  if (stops.length === 0) return r
  const priorEnd = stops[stops.length - 1]
  if (priorEnd.naptanId === undefined) return r
  if (!dstNaptanIds.has(priorEnd.naptanId)) return r
  // A trailing STREET walk (walk + empty transition_id) was never
  // counted in transfer_count (canon 2026-07: street walks count only
  // strictly between two rides) — don't decrement for it.
  const droppedWasCounted = !(last.type === 'walk' && (last.transition_id ?? '') === '')
  const truncated: Route = {
    ...r,
    legs: r.legs.slice(0, -1),
    transfer_count: r.transfer_count - (droppedWasCounted ? 1 : 0),
    total_time_s: r.total_time_s - last.duration_s,
    walking_time_s: r.walking_time_s - (last.type === 'walk' ? last.duration_s : 0),
  }
  return recomputeID(truncated)
}

export function findRoutes(
  graph: Graph,
  source: Vertex,
  target: Vertex,
  options: RouteOptions = {},
): Route[] {
  const cfg = options.config ?? DEFAULT_CONFIG
  if (source.id === target.id && source.lineID === target.lineID) {
    throw new RoutingError('SameOriginDestination')
  }
  // Pure-transit search runs WITHOUT walk-transfer edges (TASK-986):
  // post-filtering the K shortest paths of the full graph found nothing on
  // the v2 map, whose cheap out-of-station walks and platform twins fill any
  // K budget (the Tooting Broadway → Heathrow T5 tube route sat below the
  // first 64). No walks to dig past any more, so it searches the standard
  // budget — the fewest-changes pass supplies the diversity, and the old 8×
  // budget was the costliest search of the three.
  const penalty = options.transferPenaltySec ?? 0
  let paths: Edge[][]
  if (penalty > 0) {
    const search = graph.derived(`routing.transferPenalised.${penalty}`, (g) =>
      transferPenalisedGraph(g, penalty),
    )
    paths = yenK(search.graph, source, target, cfg.maxVariants * 2, cfg).map(search.realEdges)
  } else if (options.excludeWalkTransfers === true) {
    paths = yenK(
      graph.derived('routing.walkFree', walkFree),
      source,
      target,
      cfg.maxVariants * 2,
      cfg,
    )
  } else {
    paths = yenK(graph, source, target, cfg.maxVariants * 2, cfg)
  }
  if (paths.length === 0) throw new RoutingError('NoPath')
  // The fastest candidate — `paths` is in search order, which a transfer
  // penalty makes differ from real time.
  const bestTime = Math.min(...paths.map(pathWeight))

  const candidates: Route[] = []
  for (const p of paths) {
    if (ruleNoRevisit(p)) continue
    if (ruleNoNaptanIdRevisit(p)) continue
    if (ruleNoLineReentry(p)) continue
    if (ruleMaxTransfers(p, cfg)) continue
    if (ruleNoIndirectTransferWhenDirectExists(p, graph)) continue
    if (ruleNoDegenerateWalkTransfer(p, cfg)) continue
    if (ruleNoChainedWalks(p)) continue
    if (options.stationOrder !== undefined && ruleNoSameLineLoop(p, options.stationOrder, cfg))
      continue
    if (ruleMaxTimeRatio(pathWeight(p), bestTime, cfg)) continue
    candidates.push(edgesToRoute(p))
  }
  if (candidates.length === 0) throw new RoutingError('NoPath')

  // Destination-complex truncation — runs BEFORE dedup so
  // signature-equivalent variants collapse together once their trailing
  // transfer is gone.
  const dstNaptanIds = options.dstNaptanIds
  const truncated: Route[] =
    dstNaptanIds !== undefined && dstNaptanIds.size > 0
      ? candidates.map((r) => dropTrailingClusterTransfer(r, dstNaptanIds))
      : candidates

  let routes = dedupBySignature(truncated)

  // Mode classifier: 0 = walking-only, 1 = transit-with-walk,
  // 2 = pure-transit (no walk legs). Used by both the direct-wins
  // filter and the transfer-count dominance pruner so cross-class
  // routes never knock each other out.
  const modeClass = (r: Route): number => {
    const hasRide = r.legs.some((l) => l.type === 'ride')
    if (!hasRide) return 0
    const hasWalk = r.legs.some((l) => l.type === 'walk')
    return hasWalk ? 1 : 2
  }

  // Direct-route-wins post-filter: when at least one zero-transfer RIDE
  // route exists, drop multi-transfer transit alternatives — but only
  // those in the SAME mode class. A walking-only route also has
  // `transfer_count === 0` but doesn't count as "direct" here; losing
  // the tube option just because a pedestrian walk is faster
  // (Westbourne Park → Queen's Park) is wrong. Pure-transit and
  // transit-with-walking are also separate classes — a user carrying
  // luggage wants a no-walk option even when a faster walking-hybrid
  // exists.
  const hasDirectRide = routes.some(
    (r) => r.transfer_count === 0 && r.legs.some((l) => l.type === 'ride'),
  )
  if (hasDirectRide) {
    routes = routes.filter((r) => r.transfer_count === 0 || !r.legs.some((l) => l.type === 'ride'))
  }

  // Transfer-count dominance pruning: drop Y when there exists an X with
  // strictly fewer transfers such that even penalising X's total time by
  // the per-extra-transfer budget keeps it ahead of Y. The user would
  // always pick X — Y is pure UI noise.
  //
  // Mode-class guard: walking-only / walking-hybrid / pure-transit routes
  // sit in three separate dominance classes. Walking-hybrid does NOT
  // dominate pure-transit even when faster — user might be carrying
  // luggage / using a wheelchair and need the no-walk option.
  routes = routes.filter((y) => {
    const yClass = modeClass(y)
    for (const x of routes) {
      if (x === y) continue
      if (modeClass(x) !== yClass) continue
      if (x.transfer_count >= y.transfer_count) continue
      const allowedTime =
        x.total_time_s + (y.transfer_count - x.transfer_count) * cfg.transferDominancePenaltySec
      if (allowedTime <= y.total_time_s) return false
    }
    return true
  })

  routes = routes.sort((a, b) => (scoreLess(a, b) ? -1 : scoreLess(b, a) ? 1 : 0))
  return routes.length > cfg.maxVariants ? routes.slice(0, cfg.maxVariants) : routes
}

/** A copy of `graph` without its walk-transfer edges. */
function walkFree(graph: Graph): Graph {
  const out = new Graph()
  for (const edge of graph.allEdges()) {
    if (edge.type !== EdgeType.WalkTransfer) out.addEdge(edge)
  }
  return out
}
